import { cva } from 'class-variance-authority';
import * as React from 'react';
import { CollapsedBadge } from './collapsed-badge';
import { ExpandedPanel } from './expanded-panel';
import {
	FloatingPanelPositionEnum,
	FloatingPanelStateEnum,
	FloatingPanelToneEnum,
} from './types';
import type { IFloatingNotificationPanelProps } from './types';
import { cn } from '@utils';

const PANEL_Z_INDEX = 100001;
const DEFAULT_PANEL_WIDTH = '32rem';
const DEFAULT_MAX_CONTENT_HEIGHT = '40rem';

const positionVariants = cva('fixed transition-[top,bottom] duration-300', {
	variants: {
		position: {
			[FloatingPanelPositionEnum.TOP_RIGHT]: 'top-4 right-4',
			[FloatingPanelPositionEnum.TOP_LEFT]: 'top-4 left-4',
			[FloatingPanelPositionEnum.BOTTOM_RIGHT]: 'bottom-4 right-4',
			[FloatingPanelPositionEnum.BOTTOM_LEFT]: 'bottom-4 left-4',
		},
	},
	defaultVariants: {
		position: FloatingPanelPositionEnum.TOP_RIGHT,
	},
});

/**
 * `React.Children.count` treats `false`, `null`, and empty fragments as children.
 * `toArray` drops those placeholders so `{condition && <Item />}` can still show emptyState.
 */
function hasRenderableChildren(children: React.ReactNode): boolean {
	return React.Children.toArray(children).length > 0;
}

/**
 * FloatingNotificationPanel - A premium floating panel for displaying notifications.
 */
export const FloatingNotificationPanel = React.forwardRef<HTMLDivElement, IFloatingNotificationPanelProps>(
	(
		{
			state,
			position = FloatingPanelPositionEnum.TOP_RIGHT,
			labels,
			children,
			footer,
			badgeCount = 0,
			tone = FloatingPanelToneEnum.SUCCESS,
			badgeIcon,
			onBadgeClick,
			onMinimize,
			onDismiss,
			onTogglePosition,
			shouldPulse = false,
			maxContentHeight = DEFAULT_MAX_CONTENT_HEIGHT,
			panelWidth = DEFAULT_PANEL_WIDTH,
			zIndex = PANEL_Z_INDEX,
			className,
			testId = 'floating-notification-panel',
		},
		ref
	) => {
		if (state === FloatingPanelStateEnum.HIDDEN) {
			return null;
		}

		const hasChildren = hasRenderableChildren(children);
		const moveControl = onTogglePosition
			? {
					position,
					onToggle: onTogglePosition,
					moveToBottomAriaLabel: labels.moveToBottomAriaLabel,
					moveToTopAriaLabel: labels.moveToTopAriaLabel,
				}
			: undefined;

		return (
			<div
				ref={ref}
				className={cn(positionVariants({ position }), className)}
				style={{ zIndex }}
				data-testid={testId}
				data-state={state}
				data-position={position}
			>
				{state === FloatingPanelStateEnum.COLLAPSED && (
					<CollapsedBadge
						count={badgeCount}
						tone={tone}
						icon={badgeIcon}
						onClick={onBadgeClick}
						onDismiss={onDismiss}
						moveControl={moveControl}
						shouldPulse={shouldPulse}
						expandAriaLabel={labels.expandAriaLabel}
						dismissAriaLabel={labels.dismissAriaLabel}
					/>
				)}

				{state === FloatingPanelStateEnum.EXPANDED && (
					<ExpandedPanel
						labels={labels}
						tone={tone}
						onMinimize={onMinimize}
						onDismiss={onDismiss}
						moveControl={moveControl}
						maxContentHeight={maxContentHeight}
						panelWidth={panelWidth}
						hasChildren={hasChildren}
						footer={footer}
					>
						{children}
					</ExpandedPanel>
				)}
			</div>
		);
	}
);

FloatingNotificationPanel.displayName = 'FloatingNotificationPanel';
